// filterSortEvaluator.js

// Shared runtime evaluator for view filters and sorts.
// Keeps Database and NodeViewContent behavior in sync.

import {
  getAnnualDateOccurrenceForYear,
  getAnnualDateOccurrencesInRange,
  getNextAnnualDateOccurrence,
  getPreviousAnnualDateOccurrence,
} from 'utilities/AnnualDate';
import { buildEnumOptionValueRankMap } from 'utilities/EnumOptions';
import { isMeasurementValueEmpty } from 'utilities/Measurement';
import { getNodelistPrimaryKey, normalizeNodelistKeys } from 'utilities/NodelistField';
import { normalizeSchemaFieldType, isEnumCompatibleFieldType } from 'utilities/FieldTypes';

const RELATIVE_DATE_OPERATORS = new Set(['within_past', 'within_next', 'older_than', 'is_overdue', 'is_today', 'is_this']);

const toComparableDate = (value) => {
  if (value == null) return null;
  if (value && typeof value === 'object' && value.startDate) {
    return toComparableDate(value.startDate);
  }
  if (value instanceof Date) return value.getTime();
  if (value?.toDate && typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const toRangeBounds = (value) => {
  if (value == null) return null;

  if (value && typeof value === 'object' && value.startDate) {
    const start = toComparableDate(value.startDate);
    const rawEnd = value.endDate != null ? value.endDate : value.startDate;
    const end = toComparableDate(rawEnd);
    if (start == null || end == null) return null;
    return start <= end ? { start, end } : { start: end, end: start };
  }

  const point = toComparableDate(value);
  if (point == null) return null;
  return { start: point, end: point };
};

const rangesOverlap = (left, right) => (
  !!left && !!right && left.start <= right.end && left.end >= right.start
);

const normalizeRelativeUnit = (unit) => {
  if (!unit) return null;
  const normalized = String(unit).toLowerCase();
  if (normalized === 'day' || normalized === 'days') return 'days';
  if (normalized === 'week' || normalized === 'weeks') return 'weeks';
  if (normalized === 'month' || normalized === 'months') return 'months';
  if (normalized === 'year' || normalized === 'years') return 'years';
  return null;
};

const addDateUnits = (date, amount, unit) => {
  const next = new Date(date.getTime());
  if (unit === 'days') next.setDate(next.getDate() + amount);
  if (unit === 'weeks') next.setDate(next.getDate() + amount * 7);
  if (unit === 'months') next.setMonth(next.getMonth() + amount);
  if (unit === 'years') next.setFullYear(next.getFullYear() + amount);
  return next;
};

const getCurrentPeriodRange = (unit, now) => {
  if (unit === 'weeks') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start.getTime());
    end.setDate(start.getDate() + 7);
    return { start, end };
  }

  if (unit === 'months') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start, end };
  }

  if (unit === 'years') {
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear() + 1, 0, 1);
    return { start, end };
  }

  return null;
};

const isEmptyValue = (value) => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value') && Object.prototype.hasOwnProperty.call(value, 'unit')) {
    return isMeasurementValueEmpty(value);
  }
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const normalizeFilterJoin = (join) => (
  String(join || '').toLowerCase() === 'or' ? 'or' : 'and'
);

const getEffectiveFieldType = ({ fieldKey, fieldDef, explicitType }) => {
  const normalizedFieldKey = String(fieldKey || '').trim().toLowerCase();
  if (normalizedFieldKey === 'autonumber') return 'number';
  return normalizeSchemaFieldType(explicitType || fieldDef?.type || 'string', 'string');
};

const hasAnnualDateOccurrenceInRange = (value, start, endExclusive) => {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return false;
  if (!(endExclusive instanceof Date) || Number.isNaN(endExclusive.getTime())) return false;
  if (endExclusive <= start) return false;

  const rangeEnd = new Date(endExclusive.getTime() - 1);
  return getAnnualDateOccurrencesInRange(value, start, rangeEnd).length > 0;
};

export const createFilterSortEvaluator = ({
  resolveFieldValue,
  schemaFieldMap,
  viewerUserKey: rawViewerUserKey,
  resolveNodelistTitle = null,
}) => {
  const viewerUserKey = String(rawViewerUserKey || '').trim();

  const extractPersonKeys = (value) => {
    if (value == null) return [];

    const list = Array.isArray(value) ? value : [value];

    return list
      .map((entry) => {
        if (typeof entry === 'string' || typeof entry === 'number') {
          return String(entry).trim();
        }
        if (!entry || typeof entry !== 'object') return '';
        return String(entry.userKey || entry.key || '').trim();
      })
      .filter(Boolean);
  };

  // Evaluates one filter row against a single item.
  const applyFilter = (item, filter) => {
    if (!filter?.field || !filter?.operator) return true;
    const fieldDef = schemaFieldMap.get(filter.field);
    const fieldType = getEffectiveFieldType({
      fieldKey: filter.field,
      fieldDef,
      explicitType: filter?.type,
    });
    const operator = filter.operator;
    const rawValue = fieldDef ? resolveFieldValue(item, fieldDef) : item?.[filter.field];

    if (operator === 'is_empty') return isEmptyValue(rawValue);
    if (operator === 'is_not_empty') return !isEmptyValue(rawValue);

    if (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage') {
      const left = rawValue == null ? null : Number(rawValue);
      if (left == null || Number.isNaN(left)) return false;

      if (operator === 'between') {
        const rangeValue = filter?.value;
        const min = rangeValue && typeof rangeValue === 'object' ? Number(rangeValue.min) : Number.NaN;
        const max = rangeValue && typeof rangeValue === 'object' ? Number(rangeValue.max) : Number.NaN;
        if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return false;
        return left >= min && left <= max;
      }

      const right = filter.value == null ? null : Number(filter.value);
      if (right == null || Number.isNaN(right)) return false;
      if (operator === 'gt') return left > right;
      if (operator === 'gte') return left >= right;
      if (operator === 'lt') return left < right;
      if (operator === 'lte') return left <= right;
      if (operator === 'is') return left === right;
      if (operator === 'is_not') return left !== right;
      return true;
    }

    if (fieldType === 'annualdate') {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const currentYearOccurrence = getAnnualDateOccurrenceForYear(rawValue, now.getFullYear());
      const nextOccurrence = getNextAnnualDateOccurrence(rawValue, now);
      const previousOccurrence = getPreviousAnnualDateOccurrence(rawValue, now);

      if (operator === 'is_overdue') {
        return !!currentYearOccurrence && currentYearOccurrence < startOfToday;
      }

      if (operator === 'is_today') {
        return hasAnnualDateOccurrenceInRange(rawValue, startOfToday, startOfTomorrow);
      }

      if (operator === 'is_this') {
        const unit = normalizeRelativeUnit(filter.unit);
        const range = unit ? getCurrentPeriodRange(unit, now) : null;
        if (!range) return false;
        return hasAnnualDateOccurrenceInRange(rawValue, range.start, range.end);
      }

      if (operator === 'within_past' || operator === 'within_next') {
        const unit = normalizeRelativeUnit(filter.unit);
        const amount = Number(filter.value);
        if (!unit || !Number.isFinite(amount) || amount <= 0) return false;
        if (operator === 'within_past') {
          if (!previousOccurrence) return false;
          const start = addDateUnits(now, -amount, unit).getTime();
          const previousMs = previousOccurrence.getTime();
          return previousMs >= start && previousMs <= now.getTime();
        }

        if (!nextOccurrence) return false;
        const end = addDateUnits(now, amount, unit).getTime();
        const nextMs = nextOccurrence.getTime();
        return nextMs >= startOfToday.getTime() && nextMs <= end;
      }

      if (operator === 'older_than') {
        const unit = normalizeRelativeUnit(filter.unit);
        const amount = Number(filter.value);
        if (!unit || !Number.isFinite(amount) || amount <= 0 || !previousOccurrence) return false;
        const threshold = addDateUnits(now, -amount, unit).getTime();
        return previousOccurrence.getTime() < threshold;
      }

      const left = nextOccurrence?.getTime?.() ?? null;
      const right = toComparableDate(filter.value);
      if (left == null || right == null) return false;
      if (operator === 'gt') return left > right;
      if (operator === 'gte') return left >= right;
      if (operator === 'lt') return left < right;
      if (operator === 'lte') return left <= right;
      if (operator === 'is') return left === right;
      if (operator === 'is_not') return left !== right;
      return true;
    }

    if (fieldType === 'daterange' || fieldType === 'datetimerange') {
      const leftRange = toRangeBounds(rawValue);
      if (!leftRange) return false;

      const now = new Date();
      const nowMs = now.getTime();

      if (operator === 'is_active') {
        return leftRange.start <= nowMs && leftRange.end >= nowMs;
      }

      if (operator === 'is_overdue') {
        return leftRange.end < nowMs;
      }

      if (operator === 'is_today') {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
        return rangesOverlap(leftRange, { start, end });
      }

      if (operator === 'overlaps_this' || operator === 'is_this') {
        const unit = normalizeRelativeUnit(filter.unit);
        const range = unit ? getCurrentPeriodRange(unit, now) : null;
        if (!range) return false;
        return rangesOverlap(leftRange, {
          start: range.start.getTime(),
          end: range.end.getTime(),
        });
      }

      if (
        operator === 'overlaps_past'
        || operator === 'overlaps_next'
        || operator === 'within_past'
        || operator === 'within_next'
      ) {
        const unit = normalizeRelativeUnit(filter.unit);
        const amount = Number(filter.value);
        if (!unit || !Number.isFinite(amount) || amount <= 0) return false;
        if (operator === 'overlaps_past' || operator === 'within_past') {
          const start = addDateUnits(now, -amount, unit).getTime();
          return rangesOverlap(leftRange, { start, end: nowMs });
        }
        const end = addDateUnits(now, amount, unit).getTime();
        return rangesOverlap(leftRange, { start: nowMs, end });
      }

      const right = toComparableDate(filter.value);
      if (right == null) return false;
      const left = leftRange.start;
      if (operator === 'gt') return left > right;
      if (operator === 'gte') return left >= right;
      if (operator === 'lt') return left < right;
      if (operator === 'lte') return left <= right;
      if (operator === 'is') return left === right;
      if (operator === 'is_not') return left !== right;
      return true;
    }

    if (fieldType === 'date' || fieldType === 'datetime') {
      const left = toComparableDate(rawValue);
      if (left == null) return false;

      if (RELATIVE_DATE_OPERATORS.has(operator)) {
        const now = new Date();
        const nowMs = now.getTime();

        if (operator === 'is_overdue') return left < nowMs;

        if (operator === 'is_today') {
          const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
          return left >= start.getTime() && left < end.getTime();
        }

        if (operator === 'is_this') {
          const unit = normalizeRelativeUnit(filter.unit);
          const range = unit ? getCurrentPeriodRange(unit, now) : null;
          if (!range) return false;
          return left >= range.start.getTime() && left < range.end.getTime();
        }

        if (operator === 'within_past' || operator === 'within_next') {
          const unit = normalizeRelativeUnit(filter.unit);
          const amount = Number(filter.value);
          if (!unit || !Number.isFinite(amount) || amount <= 0) return false;
          if (operator === 'within_past') {
            const start = addDateUnits(now, -amount, unit).getTime();
            return left >= start && left <= nowMs;
          }
          const end = addDateUnits(now, amount, unit).getTime();
          return left >= nowMs && left <= end;
        }

        if (operator === 'older_than') {
          const unit = normalizeRelativeUnit(filter.unit);
          const amount = Number(filter.value);
          if (!unit || !Number.isFinite(amount) || amount <= 0) return false;
          const threshold = addDateUnits(now, -amount, unit).getTime();
          return left < threshold;
        }
      }

      const right = toComparableDate(filter.value);
      if (right == null) return false;
      if (operator === 'gt') return left > right;
      if (operator === 'gte') return left >= right;
      if (operator === 'lt') return left < right;
      if (operator === 'lte') return left <= right;
      if (operator === 'is') return left === right;
      if (operator === 'is_not') return left !== right;
      return true;
    }

    if (fieldType === 'boolean') {
      const left = Boolean(rawValue);
      const right = filter.value === true || filter.value === 'true';
      if (operator === 'is') return left === right;
      if (operator === 'is_not') return left !== right;
      return true;
    }

    if (fieldType === 'people') {
      const keys = extractPersonKeys(rawValue);
      const hasPeople = keys.length > 0;

      if (operator === 'is_me') {
        if (!viewerUserKey) return false;
        return keys.includes(viewerUserKey);
      }

      if (operator === 'is_not_me') {
        if (!viewerUserKey) return false;
        if (!hasPeople) return true;
        return !keys.includes(viewerUserKey);
      }

      if (operator === 'is_empty') return !hasPeople;
      if (operator === 'is_not_empty') return hasPeople;
      return true;
    }

    if (isEnumCompatibleFieldType(fieldType) || fieldType === 'multiselect' || fieldType === 'tags') {
      const values = (Array.isArray(rawValue) ? rawValue : [rawValue])
        .map((entry) => String(entry == null ? '' : entry).trim().toLowerCase())
        .filter(Boolean);

      if (operator === 'is_any_of' || operator === 'is_none_of') {
        const filterValues = (Array.isArray(filter.value) ? filter.value : [filter.value])
          .map((entry) => String(entry == null ? '' : entry).trim().toLowerCase())
          .filter(Boolean);
        if (!filterValues.length) return operator === 'is_none_of';
        const matched = filterValues.some((value) => values.includes(value));
        return operator === 'is_any_of' ? matched : !matched;
      }

      const right = String(filter.value == null ? '' : filter.value).trim().toLowerCase();
      if (!right) return false;
      const matched = values.includes(right);
      if (operator === 'is') return matched;
      if (operator === 'is_not') return !matched;
      return true;
    }

    if (fieldType === 'relation') {
      const keys = normalizeNodelistKeys(rawValue);
      const hasKeys = keys.length > 0;

      if (operator === 'is_empty') return !hasKeys;
      if (operator === 'is_not_empty') return hasKeys;

      if (operator === 'is_any_of' || operator === 'is_none_of') {
        const filterKeys = normalizeNodelistKeys(filter.value);
        if (!filterKeys.length) return operator === 'is_none_of';
        const matched = filterKeys.some((key) => keys.includes(key));
        return operator === 'is_any_of' ? matched : !matched;
      }

      const right = String(filter.value == null ? '' : filter.value).trim();
      if (!right) return false;
      const matched = keys.includes(right);
      if (operator === 'is') return matched;
      if (operator === 'is_not') return !matched;
      return true;
    }

    const left = rawValue == null ? '' : String(rawValue).toLowerCase();
    const right = filter.value == null ? '' : String(filter.value).toLowerCase();

    if (operator === 'contains') return left.includes(right);
    if (operator === 'starts_with') return left.startsWith(right);
    if (operator === 'ends_with') return left.endsWith(right);
    if (operator === 'is') return left === right;
    if (operator === 'is_not') return left !== right;
    return true;
  };

  // Evaluates all filter rows with OR chains grouped together, then ANDed between groups.
  const applyFilters = (item, filters) => {
    if (!Array.isArray(filters) || filters.length === 0) return true;

    let hasGroup = false;
    let currentGroupMatch = false;

    for (let index = 0; index < filters.length; index += 1) {
      const filter = filters[index];
      const rowMatch = applyFilter(item, filter);

      if (!hasGroup) {
        hasGroup = true;
        currentGroupMatch = rowMatch;
        continue;
      }

      const join = normalizeFilterJoin(filters[index - 1]?.joinToNext);
      if (join === 'or') {
        currentGroupMatch = currentGroupMatch || rowMatch;
        continue;
      }

      if (!currentGroupMatch) return false;
      currentGroupMatch = rowMatch;
    }

    return !hasGroup || currentGroupMatch;
  };

  // Applies multi-column sort definitions to a list of items.
  const applySorts = (items, sorts) => {
    if (!Array.isArray(items) || !Array.isArray(sorts) || sorts.length === 0) return items;
    const normalizedSorts = sorts
      .filter((sort) => sort?.field)
      .map((sort) => {
        const fieldDef = schemaFieldMap.get(sort.field);
        const fieldType = getEffectiveFieldType({
          fieldKey: sort.field,
          fieldDef,
        });

        return {
          direction: sort?.direction === 'desc' ? -1 : 1,
          enumValueRanks: isEnumCompatibleFieldType(fieldType)
            ? buildEnumOptionValueRankMap(fieldDef?.options || [])
            : null,
          fieldDef,
          fieldKey: sort.field,
          fieldType,
        };
      });

    if (!normalizedSorts.length) return items;

    const sorted = [...items];

    sorted.sort((a, b) => {
      for (const sort of normalizedSorts) {
        const {
          direction,
          enumValueRanks,
          fieldDef,
          fieldKey,
          fieldType,
        } = sort;

        const aRaw = fieldDef ? resolveFieldValue(a, fieldDef) : a?.[fieldKey];
        const bRaw = fieldDef ? resolveFieldValue(b, fieldDef) : b?.[fieldKey];

        if (isEnumCompatibleFieldType(fieldType)) {
          const aValue = aRaw == null ? '' : String(aRaw).trim();
          const bValue = bRaw == null ? '' : String(bRaw).trim();

          if (!aValue && !bValue) continue;
          if (!aValue) return 1;
          if (!bValue) return -1;

          const aHasRank = enumValueRanks.has(aValue);
          const bHasRank = enumValueRanks.has(bValue);

          if (aHasRank && bHasRank) {
            const byRank = enumValueRanks.get(aValue) - enumValueRanks.get(bValue);
            if (byRank !== 0) return byRank * direction;
          } else if (aHasRank !== bHasRank) {
            return aHasRank ? -1 : 1;
          }

          const byValue = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
          if (byValue !== 0) return byValue * direction;
          continue;
        }

        if (fieldType === 'relation') {
          const aKey = getNodelistPrimaryKey(aRaw);
          const bKey = getNodelistPrimaryKey(bRaw);

          if (!aKey && !bKey) continue;
          if (!aKey) return 1;
          if (!bKey) return -1;

          const aLabel = typeof resolveNodelistTitle === 'function'
            ? String(resolveNodelistTitle(aKey) || aKey)
            : aKey;
          const bLabel = typeof resolveNodelistTitle === 'function'
            ? String(resolveNodelistTitle(bKey) || bKey)
            : bKey;

          const byLabel = aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
          if (byLabel !== 0) return byLabel * direction;
          continue;
        }

        if (aRaw == null && bRaw == null) continue;
        if (aRaw == null) return 1;
        if (bRaw == null) return -1;

        let comparison = 0;
        if (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage') {
          const aNum = Number(aRaw);
          const bNum = Number(bRaw);
          if (Number.isNaN(aNum) || Number.isNaN(bNum)) continue;
          comparison = aNum - bNum;
        } else if (fieldType === 'annualdate') {
          const aDate = getNextAnnualDateOccurrence(aRaw);
          const bDate = getNextAnnualDateOccurrence(bRaw);
          if (!aDate || !bDate) continue;
          comparison = aDate.getTime() - bDate.getTime();
        } else if (fieldType === 'date' || fieldType === 'daterange' || fieldType === 'datetime' || fieldType === 'datetimerange') {
          const aDate = toComparableDate(aRaw);
          const bDate = toComparableDate(bRaw);
          if (aDate == null || bDate == null) continue;
          comparison = aDate - bDate;
        } else if (fieldType === 'boolean') {
          comparison = (Boolean(aRaw) === Boolean(bRaw)) ? 0 : (Boolean(aRaw) ? 1 : -1);
        } else {
          comparison = String(aRaw).localeCompare(String(bRaw), undefined, { sensitivity: 'base' });
        }

        if (comparison !== 0) return comparison * direction;
      }
      return 0;
    });

    return sorted;
  };

  return { applyFilter, applyFilters, applySorts };
};
